import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { Paper, Stack, Typography } from '@mui/material'
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab'
import TimelineOppositeContent, { timelineOppositeContentClasses } from '@mui/lab/TimelineOppositeContent'
import { Container, Col, Row } from 'react-bootstrap'
import { AiOutlineSolution } from "react-icons/ai"
import { FaFileImage, FaFileVideo, FaMicrophoneAlt, FaMicrophoneAltSlash } from "react-icons/fa"
import { IoMdDownload } from "react-icons/io"
import { IoFilter } from "react-icons/io5"
import { FaRegSquareCheck } from "react-icons/fa6"
import { GoIssueReopened, GoReport } from "react-icons/go"
import { RxActivityLog } from "react-icons/rx"
import { PiExportBold } from "react-icons/pi"

import { Form, TreeSelect } from 'antd'
import { useSnackbar } from 'notistack'
import moment from 'moment'
import { saveAs } from 'file-saver'

import DataTable from 'Shared/Components/DataTable'
import { FormContainer, CheckList, DatePicker } from 'Shared/Components/FormComponents'
import { IconButtonComponent } from 'Shared/Components/Icon'
import PlaybackModal from './components/PlaybackModal'
import PopupModal from 'Shared/Components/PopupModal'
import StatusChip from './components/StatusChip'
import { showSuccess } from 'Shared/Components/NotifyToast'
import useFetchData from 'Shared/Hooks/useFetchData'
import useLoop from 'Shared/Hooks/useLoop'
import { apiUrl, webUrl } from 'Shared/utils'

const activityLogComponents = {
  "acknowledge": {
    "icon": <AiOutlineSolution />,
    "label": "alert_acknowledge"
  },
  "resolve": {
    "icon": <FaRegSquareCheck />,
    "label": "alert_resolve"
  },
  "reopen": {
    "icon": <GoIssueReopened />,
    "label": "alert_reopen"
  },
  "invalid": {
    "icon": <GoReport />,
    "label": "alert_invalid"
  }
}

const Alert = () => {
  const { role, setPageLoading, alertTypes, alertEvents, projectInfo } = useOutletContext()

  const { t } = useTranslation()
  const { send } = useFetchData()
  const [form] = Form.useForm()
  const { enqueueSnackbar } = useSnackbar()
  const [alertData, setAlertData] = useState({})
  const [alertSnapshot, setAlertSnapshot] = useState(null)
  const [alertVideo, setAlertVideo] = useState(null)
  const [filterFormOpen, setFilterFormOpen] = useState(false)
  const [showHistory, setShowHistory] = useState()
  const [sirenStatus, setSirenStatus] = useState(false)

  const tableRef = useRef()

  const filterRef = useRef({
    "search": [],
    "date": [],
    "status": [],
    "sirenStatus": false,
    "lastId": null
  })

  const playSound = () => {
    const audio = new Audio(`${webUrl}/siren.wav`);
    audio.play().catch(error => console.error('Error playing audio:', error));

    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
    }, 10000);
  };

  const getAlertData = async (showPopup) => {        
    const fetchData = async () => {
      const body = await send({
        method: "GET",
        url: `${apiUrl}/alert/history`,
        params: {
          "offset": (tableRef.current.getState()["page"]-1)*tableRef.current.getState()["itemsPerPage"],
          "limit": tableRef.current.getState()["itemsPerPage"],
          "search": filterRef.current.search.join("|"),
          "date": filterRef.current.date.map((i) => moment(new Date(i)).format("YYYY-MM-DD")).join("|"),
          "status": filterRef.current.status.join("|"),
          "last_id": filterRef.current.lastId ?? ""
        },
        returnType: "json"
      })
  
      return body
    }

    const getAlertDetail = (item) => {
      const getSnapshot = async (item) => {
        setAlertSnapshot(item)
      }
  
      const getVideo = async (item) => {
        setAlertVideo(item)
      }
  
      const download = async (item) => {
        const id = item["record_id"]

        setPageLoading(true)
  
        const blob = await send({
          method: "GET",
          url: `${apiUrl}/ai_playback/download`,
          params: {
            "record_id": id
          },
          returnType: "blob"
        })
  
        if (blob != null) {
          saveAs(blob, `${id}.zip`)
        }
  
        setPageLoading(false)
      }
  
      switch (item[["type"]]) {
        case "ai_detection":
        case "proximity":
          return [
            {icon: <FaFileImage size={15}/>, text: t("alert_snapshot"), onClick: function(item) { getSnapshot(item) }},
            {icon: <FaFileVideo size={15}/>, text: t("alert_video"), onClick: function(item) { getVideo(item) }},
            ["pm", "so", "admin"].includes(role) ? {icon: <IoMdDownload size={15}/>, text: t("general_download"), onClick: function(item) { download(item)}} : null
          ]
            
        default:
          return []
      }
    }
  
    const formatAlertDetail = (item) => {
      const actions = getAlertDetail(item)

      return (<>
        {(actions.length > 0) ? (
          <Stack justifyContent="center" direction="row" spacing={0}>
            {actions.map((action) => <>
              {action && 
                <IconButtonComponent title={action.text} onClick={() => action.onClick(item) }>
                  {action.icon}
                </IconButtonComponent>
              }
            </>)}
          </Stack>
        ) : (
          <div>-</div>
        )}
      </>)
    }

    const body = await fetchData()

    const data = []
    const selectInitial = {}

    const selectState = tableRef.current.getState()["selected"]

    body["data"].forEach((i) => {
      const id = new String(i.record_id).toString()

      data.push({
        "id": id,
        "datetime": i["datetime"],
        "message": `${t(alertTypes[i["type"]])} (${t(alertEvents[i["type"]][i["event"]])})`,
        "target": i["target"],
        "status": <StatusChip status={i["status"]} />,
        "detail": formatAlertDetail(i)
      })

      selectInitial[id] = showPopup ? (selectState[id] === true) : false 
    })

    if (body["recent_records"].length > 0) {
      if (filterRef.current.sirenStatus) {
        playSound()
      }

      body["recent_records"].forEach((i) => {
        const message = `${t(alertTypes[i["type"]])} (${t(alertEvents[i["type"]][i["event"]])})`
        const target = i["target"] ?? ""

        enqueueSnackbar(`${message} | ${target}`, { variant: 'warningToast' })
      })
    }

    if (body["last_id"]) {
      filterRef.current.lastId = body["last_id"]
    }

    setAlertData({
      "data": data,
      "count": Math.ceil(body["count"]/tableRef.current.getState()["itemsPerPage"])
    })

    tableRef.current.setSelect(selectInitial)
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await Promise.all([
        getAlertData(false)
      ])

      setPageLoading(false)
    }

    init()
  }, [])

  const loadData = async () => {
    tableRef.current.setTableLoading(true)

    await getAlertData(false)

    tableRef.current.setTableLoading(false)
  }

  const update = async (action, records) => {
    setPageLoading(true)

    records = records ?? Object.entries(tableRef.current.getState()["selected"]).filter(([k, v]) => v === true).map(([k, v]) => k)

    const res = await send({
      method: "POST",
      url: `${apiUrl}/alert/update/${action}`,
      contentType: "json",
      body: {
        "records": JSON.stringify(records)
      }
    })

    if (res != null) {
      showSuccess(t("general_success"))

      tableRef.current.setTableLoading(true)

      setAlertSnapshot()
      setAlertVideo()

      await loadData()

      tableRef.current.setTableLoading(false)
    }

    setPageLoading(false)
  }

  const showActivities = async (record_id) => {
    setPageLoading(true)

    const body = await send({
      method: "GET",
      url: `${apiUrl}/alert/activity_log`,
      returnType: "json",
      params: {
        "record_id": record_id
      }
    })

    setShowHistory(body)

    setPageLoading(false)
  }
  
  const onFinish = (value) => {
    filterRef.current.search = value["items"] ?? []
    filterRef.current.date = value["date"] ?? []
    filterRef.current.status = value["status"] ?? []

    setFilterFormOpen(false)
    tableRef.current.setPage(1)

    loadData()
  }

  const report = async () => {
    setPageLoading(true)

    const blob = await send({
      method: "GET",
      url: `${apiUrl}/alert/report`,
      params: {
        "search": filterRef.current.search.join("|"),
        "date": filterRef.current.date.map((i) => moment(new Date(i)).format("YYYY-MM-DD")).join("|"),
        "status": filterRef.current.status.join("|"),
      },
      returnType: "blob"
    })

    if (blob != null) {
      saveAs(blob, `export.xlsx`)
    }

    setPageLoading(false)
  }

  useLoop(async function() {
    getAlertData(true)
  }, 10000)

  return (<>
    <Container fluid className="mainContainer" style={{ backgroundColor: "white", borderRadius: "15px" }}>
      <Row style={{ height: "100%" }}>
        <Col xs={12} style={{ height: "100%", overflow: "auto", padding: "10px" }}>
          <Paper elevation={0} sx={{ height: "100%", display: "flex", flexFlow: "column" }}>
            <DataTable
              data={alertData["data"]} count={alertData["count"]} loadData={() => loadData()} ref={tableRef}
              showCheck={["pm", "so", "admin"].includes(role)}
              columns={{
                "datetime": { "label": t("general_datetime"), "sorting": false, "nowrap": true },
                "message": { "label": t("alert_message"), "sorting": false, "nowrap": true },
                "target": { "label": t("alert_target"), "sorting": false, "nowrap": true },
                "detail": { "label": t("alert_detail"), "sorting": false, "nowrap": true },
                "status": { "label": t("alert_status"), "sorting": false, "nowrap": true },
              }}
              appActions={() => { return [
                (["admin"].includes(role)) && {icon: sirenStatus ? <FaMicrophoneAlt size={20}/> : <FaMicrophoneAltSlash size={20}/>, text: sirenStatus ? t("alert_sirenOff") : t("alert_sirenOn"), onClick: function() { filterRef.current.sirenStatus = !sirenStatus; setSirenStatus(!sirenStatus) }},
                {icon: <IoFilter size={20}/>, text: t("general_filter"), onClick: function() { form.setFieldsValue({ "items": filterRef.current.search, "date": filterRef.current.date }); setFilterFormOpen(true); }},
                ["pm", "so", "admin"].includes(role) && {icon: <PiExportBold size={20}/>, text: t("general_export"), onClick: function() { report() }}
              ]}}
              renderActions={(row) => { return [
                ["pm", "so", "admin"].includes(role) && {icon: <RxActivityLog size={15}/>, text: t("general_activityLog"), onClick: function(row) { showActivities(row["id"]) }},
              ]}}
              selectActions={() => { return [
                ["pm", "so", "admin"].includes(role) && {icon: <AiOutlineSolution size={20}/>, text: t("alert_acknowledge"), onClick: function() { update("acknowledge") }},
                ["pm", "so", "admin"].includes(role) && {icon: <FaRegSquareCheck size={20}/>, text: t("alert_resolve"), onClick: function() { update("resolve") }},
                ["pm", "so", "admin"].includes(role) && {icon: <GoReport size={20}/>, text: t("alert_invalid"), onClick: function() { update("invalid") }},
                ["pm", "so", "admin"].includes(role) && {icon: <GoIssueReopened size={20}/>, text: t("alert_reopen"), onClick: function() { update("reopen") }}
              ]}}
            />
          </Paper>
        </Col>
      </Row>
    </Container>

    <PlaybackModal type="snapshot" item={alertSnapshot} update={update} toggle={() => setAlertSnapshot(null)}/>
    <PlaybackModal type="video" item={alertVideo} update={update} toggle={() => setAlertVideo(null)}/>

    <PopupModal isOpen={filterFormOpen} close toggle={() => setFilterFormOpen(false)} height="80%" width="80%">
      <FormContainer form={form} onFinish={onFinish} initialValues={{}}>
        <DatePicker field_name={t("alert_searchDate")} field_id="date" props={{ "max": new Date(), "range": true }}/>

        <Form.Item label={t("alert_searchItems")} name="items">
          <TreeSelect
            treeData={Object.entries(alertEvents).filter(([k1, v1]) => projectInfo["modules"].filter((i) => i["enabled"] === true).map((i) => i["module"]).includes(k1)).map(([k1, v1]) =>({
              "title": t(alertTypes[k1]), "value": k1, "key": k1,
              "children": Object.entries(v1).map(([k2, v2]) => (
                { "title": t(v2), "value": k2, "key": k2 }   
              ))
            }))}
            treeCheckable={true}
            showCheckedStrategy={TreeSelect.SHOW_PARENT}
            style={{ width: '100%' }}
            dropdownStyle={{ zIndex: 1500 }}
          />
        </Form.Item>

        <CheckList field_name={t("alert_status")} field_id="status" props={{ "multiple": true, "options": [
          { label: t("alert_pending"), value: '0' },
          { label: t("alert_acknowledged"), value: '1' },
          { label: t("alert_resolved"), value: '2' },
          { label: t("alert_invalid"), value: '-1' }
        ]}} />
      </FormContainer>
    </PopupModal>

    <PopupModal isOpen={showHistory != null} close toggle={() => setShowHistory()} width="50%" maxHeight="60%">
      {showHistory && <>
        {(showHistory.length > 0) ? (
          <Timeline
            sx={{
              [`& .${timelineOppositeContentClasses.root}`]: { flex: 0.2 },
            }}
          >
            {Array.from(showHistory.entries()).map(([idx, i]) =>
              <TimelineItem>
                <TimelineOppositeContent color="textSecondary"></TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot>
                    {activityLogComponents[i["activity"]]["icon"]}
                  </TimelineDot>

                  {(idx < showHistory.length - 1) &&
                    <TimelineConnector />
                  }
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Typography variant="body1">{t(activityLogComponents[i["activity"]]["label"])}</Typography>
                  <Typography variant="caption">{i["performed_by"]}, {i["updated_at"]}</Typography>
                </TimelineContent>
              </TimelineItem>
            )}
          </Timeline>
        ) : (
          <div style={{ padding: "20px", width: "100%", display: "flex", justifyContent: "center" }}>
            <div><b>No Activity Found</b></div>
          </div>
        )}
      </>}
    </PopupModal>
  </>)
}

export default Alert
